import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Send,
  Bot,
  User,
  Loader2,
  MessageSquare,
  Lightbulb,
  FileText,
  HelpCircle,
} from 'lucide-react';
import { intakeService } from '@/services/api/intake';
import { cn } from '@/lib/utils';
import type { IntakeChatMessage } from '@/types/intake';

const DEMO_CLIENT_ID = 'client-1';

const SUGGESTED_QUESTIONS = [
  'What documents do I still need to upload?',
  'How long does the intake process take?',
  'What format should my documents be in?',
  'Can I update my documents after submission?',
];

export function CRMChatPage() {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch chat messages
  const { data: messages = [], isLoading, refetch } = useQuery({
    queryKey: ['chat-messages', DEMO_CLIENT_ID],
    queryFn: () => intakeService.getChatMessages(DEMO_CLIENT_ID),
  });

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: (content: string) => intakeService.sendChatMessage(DEMO_CLIENT_ID, content),
    onSuccess: () => {
      refetch();
    },
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessageMutation.isPending]);

  const handleSend = async () => {
    if (!input.trim() || sendMessageMutation.isPending) return;

    const content = input.trim();
    setInput('');

    // Optimistically add user message
    await sendMessageMutation.mutateAsync(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (question: string) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="space-y-2 mb-4">
        <h1 className="text-2xl md:text-3xl font-bold">Support Chat</h1>
        <p className="text-muted-foreground">
          Get instant answers about your intake process from our AI assistant.
        </p>
      </div>

      <div className="flex-1 grid gap-4 lg:grid-cols-[1fr_300px]">
        {/* Chat Area */}
        <Card className="flex flex-col">
          <CardHeader className="border-b py-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Intake Assistant</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Available 24/7 to help you
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 p-0 flex flex-col">
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className={cn('flex gap-3', i % 2 === 0 && 'justify-end')}>
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <Skeleton className="h-20 w-64" />
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mb-4">
                    <MessageSquare className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">Start a Conversation</h3>
                  <p className="text-sm text-muted-foreground max-w-sm mb-6">
                    Ask me anything about your intake process, required documents, or get help with uploads.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {SUGGESTED_QUESTIONS.slice(0, 2).map((question, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => handleSuggestionClick(question)}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Welcome message */}
                  <div className="flex gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        <Bot className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-1 max-w-[80%]">
                      <div className="rounded-lg bg-muted px-4 py-2">
                        <p className="text-sm">
                          Hello! I'm here to help you with your intake process. Feel free to ask me about required documents, upload formats, or any other questions you might have.
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">Intake Assistant</p>
                    </div>
                  </div>

                  {/* Chat messages */}
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        'flex gap-3',
                        message.role === 'user' && 'justify-end'
                      )}
                    >
                      {message.role !== 'user' && (
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            <Bot className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div className={cn(
                        'space-y-1 max-w-[80%]',
                        message.role === 'user' && 'items-end'
                      )}>
                        <div className={cn(
                          'rounded-lg px-4 py-2',
                          message.role === 'user'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        )}>
                          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatTime(message.createdAt)}
                        </p>
                        {/* Suggested actions */}
                        {message.role === 'assistant' && message.metadata?.suggestedActions && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {message.metadata.suggestedActions.map((action, i) => (
                              <Button key={i} variant="outline" size="sm" className="text-xs h-7">
                                {action}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                      {message.role === 'user' && (
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-blue-500/10 text-blue-500 text-xs">
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {sendMessageMutation.isPending && (
                    <div className="flex gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs">
                          <Bot className="h-4 w-4" />
                        </AvatarFallback>
                      </Avatar>
                      <div className="rounded-lg bg-muted px-4 py-2">
                        <div className="flex gap-1">
                          <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Input */}
            <div className="border-t p-4">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Type your message..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sendMessageMutation.isPending}
                  className="flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || sendMessageMutation.isPending}
                >
                  {sendMessageMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Suggestions */}
        <div className="hidden lg:flex flex-col gap-4">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                Suggested Questions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {SUGGESTED_QUESTIONS.map((question, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  className="w-full justify-start text-left h-auto py-2 px-3 text-sm font-normal"
                  onClick={() => handleSuggestionClick(question)}
                >
                  {question}
                </Button>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-500" />
                Quick Topics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="ghost"
                className="w-full justify-start h-auto py-2 px-3 text-sm font-normal"
                onClick={() => handleSuggestionClick('Tell me about the document requirements')}
              >
                Document Requirements
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-auto py-2 px-3 text-sm font-normal"
                onClick={() => handleSuggestionClick('What is the intake timeline?')}
              >
                Intake Timeline
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-auto py-2 px-3 text-sm font-normal"
                onClick={() => handleSuggestionClick('How is my data protected?')}
              >
                Data Security
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <HelpCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-primary">Need human support?</p>
                  <p className="text-muted-foreground mt-1">
                    Contact us at{' '}
                    <a href="mailto:support@data-corner.com.au" className="text-primary hover:underline">
                      support@data-corner.com.au
                    </a>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
